import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { BaseDetailComponent, CustomFieldConfig, GetCategoryList, ModalService, Permission } from '@ishop/admin-ui/core';
import {
    Channel,
    CreateChannelInput,
    CurrencyCode,
    GetZones,
    LanguageCode,
    UpdateChannelInput,
    GetChannel
} from '@ishop/admin-ui/core';
import { getDefaultUiLanguage } from '@ishop/admin-ui/core';
import { NotificationService } from '@ishop/admin-ui/core';
import { Asset, DataService } from '@ishop/admin-ui/core';
import { ServerConfigService } from '@ishop/admin-ui/core';
import { DEFAULT_CHANNEL_CODE } from '@ishop/common/lib/shared-constants';
import { EMPTY, from, Observable } from 'rxjs';
import { concatMap, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { SelectVendorCategoryDialogComponent } from '../select-vendor-category-dialog/select-vendor-category-dialog.component';

@Component({
    selector: 'vdr-channel-detail',
    templateUrl: './channel-detail.component.html',
    styleUrls: ['./channel-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelDetailComponent
    extends BaseDetailComponent<Channel.Fragment>
    implements OnInit, OnDestroy {
    customFields: CustomFieldConfig[];
    zones$: Observable<GetZones.Zones[]>;
    detailForm: FormGroup;
    currencyCodes = Object.values(CurrencyCode);
    availableLanguageCodes$: Observable<LanguageCode[]>;
    assetChanges: { assets?: Asset[]; featuredAsset?: Asset } = {};
  
    readonly updatePermission = [Permission.SuperAdmin, Permission.UpdateChannel, Permission.CreateChannel];

    constructor(
        router: Router,
        route: ActivatedRoute,
        protected serverConfigService: ServerConfigService,
        private changeDetector: ChangeDetectorRef,
        protected dataService: DataService,
        private formBuilder: FormBuilder,
        private notificationService: NotificationService,
        private modalService: ModalService,
    ) {
        super(route, router, serverConfigService, dataService);
        this.customFields = this.getCustomFieldConfig('Channel');
        this.detailForm = this.formBuilder.group({
            name: ['', Validators.required],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            description: [''],
            address: [''],
            pincode: [''],
            email: ['', Validators.required],
            officePhone: [''],
            code: ['', Validators.required],
            token: ['', Validators.required],
            pricesIncludeTax: [false],
            currencyCode: [''],
            defaultShippingZoneId: ['', Validators.required],
            defaultLanguageCode: [],
            defaultTaxZoneId: ['', Validators.required],

            customFields: this.formBuilder.group(
                this.customFields.reduce((hash, field) => ({ ...hash, [field.name]: '' }), {}),
            ),
        });
    }

    ngOnInit() {
        this.init();
        this.zones$ = this.dataService.settings.getZones().mapSingle(data => data.zones);
        this.availableLanguageCodes$ = this.serverConfigService.getAvailableLanguages();
    }

    ngOnDestroy() {
        this.destroy();
    }

    customFieldIsSet(name: string): boolean {
        return !!this.detailForm.get(['customFields', name]);
    }

    saveButtonEnabled(): boolean {
        return (this.detailForm.dirty && this.detailForm.valid) || this.assetsChanged();
    }
    assetsChanged(): boolean {
        return !!Object.values(this.assetChanges).length;
    }
    create() {
        if (!this.detailForm.dirty) {
            return;
        }
        const formValue = this.detailForm.value;
        const input: CreateChannelInput = {
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            name: formValue.name,
            description: formValue.description,
            email: formValue.email,
            officePhone: formValue.officePhone,
            address: formValue.address,
            pincode: formValue.pincode,
            code: formValue.code,
            token: formValue.token,
            defaultLanguageCode: formValue.defaultLanguageCode,
            pricesIncludeTax: formValue.pricesIncludeTax,
            currencyCode: formValue.currencyCode,
            defaultShippingZoneId: formValue.defaultShippingZoneId,
            defaultTaxZoneId: formValue.defaultTaxZoneId,
            customFields: formValue.customFields,
        };
        this.dataService.settings
            .createChannel(input)
            .pipe(
                mergeMap(({ createChannel }) =>
                    this.dataService.auth.currentUser().single$.pipe(
                        map(({ me }) => ({
                            me,
                            createChannel,
                        })),
                    ),
                ),
                mergeMap(({ me, createChannel }) =>
                    // tslint:disable-next-line:no-non-null-assertion
                    this.dataService.client.updateUserChannels(me!.channels).pipe(map(() => createChannel)),
                ),
            )
            .subscribe(data => {
                switch (data.__typename) {
                    case 'Channel':
                        this.notificationService.success(_('common.notify-create-success'), {
                            entity: 'Channel',
                        });
                        this.detailForm.markAsPristine();
                        this.changeDetector.markForCheck();
                        this.router.navigate(['../', data.id], { relativeTo: this.route });
                        break;
                    case 'LanguageNotAvailableError':
                        this.notificationService.error(data.message);
                        break;
                }
            });
    }

    save() {
        if (!this.detailForm.dirty && !this.assetsChanged()) {
            return;
        }
        const formValue = this.detailForm.value;
        this.entity$
            .pipe(
                take(1),
                mergeMap(channel => {
                    const input = {
                        id: channel.id,
                        firstName: formValue.firstName,
                        lastName: formValue.lastName,
                        name: formValue.name,
                        description: formValue.description,
                        email: formValue.email,
                        officePhone: formValue.officePhone,
                        address: formValue.address,
                        pincode: formValue.pincode,
                        code: formValue.code,
                        token: formValue.token,
                        assetIds: this.assetChanges.assets?.map(a => a.id),
                        featuredAssetId: this.assetChanges.featuredAsset?.id,
                        pricesIncludeTax: formValue.pricesIncludeTax,
                        currencyCode: formValue.currencyCode,
                        defaultShippingZoneId: formValue.defaultShippingZoneId,
                        defaultLanguageCode: formValue.defaultLanguageCode,
                        defaultTaxZoneId: formValue.defaultTaxZoneId,
                        customFields: formValue.customFields,
                    } as UpdateChannelInput;
                    return this.dataService.settings.updateChannel(input);
                }),
            )
            .subscribe(({ updateChannel }) => {
                switch (updateChannel.__typename) {
                    case 'Channel':
                        this.notificationService.success(_('common.notify-update-success'), {
                            entity: 'Channel',
                        });
                        this.detailForm.markAsPristine();
                        this.changeDetector.markForCheck();
                        break;
                    case 'LanguageNotAvailableError':
                        this.notificationService.error(updateChannel.message);
                }
            });
    }

    /**
     * Update the form values when the entity changes.
     */
    protected setFormValues(entity: Channel.Fragment, languageCode: LanguageCode): void {
        this.detailForm.patchValue({
            firstName: entity.firstName,
            lastName: entity.lastName,
            name: entity.name,
            description: entity.description,
            email: entity.email,
            officePhone: entity.officePhone,
            address: entity.address,
            pincode: entity.pincode,
            code: entity.code,
            token: entity.token || this.generateToken(),
            pricesIncludeTax: entity.pricesIncludeTax,
            currencyCode: entity.currencyCode,
            defaultShippingZoneId: entity.defaultShippingZone ? entity.defaultShippingZone.id : '',
            defaultLanguageCode: entity.defaultLanguageCode,
            defaultTaxZoneId: entity.defaultTaxZone ? entity.defaultTaxZone.id : '',
        });
        if (this.customFields.length) {
            const customFieldsGroup = this.detailForm.get('customFields') as FormGroup;

            for (const fieldDef of this.customFields) {
                const key = fieldDef.name;
                const value = (entity as any).customFields[key];
                const control = customFieldsGroup.get(key);
                if (control) {
                    control.patchValue(value);
                }
            }
        }
        if (entity.code === DEFAULT_CHANNEL_CODE) {
            const codeControl = this.detailForm.get('code');
            if (codeControl) {
                codeControl.disable();
            }
        }
    }

    private generateToken(): string {
        const randomString = () => Math.random().toString(36).substr(3, 10);
        return `${randomString()}${randomString()}`;
    }

    addToCategory() {
        this.modalService
            .fromComponent(SelectVendorCategoryDialogComponent, {
                size: 'md',
            })
            .pipe(
                switchMap(groupIds => (groupIds ? from(groupIds) : EMPTY)),
                concatMap(groupId => this.dataService.settings.addVendorsToCategory(groupId, [this.id])),
            )
            .subscribe({
                next: res => {
                    this.notificationService.success(_(`settings.add-vendors-to-category-success`), {
                        vendorCount: 1,
                        categoryName: res.addVendorsToCategory.name,
                    });
                },
                complete: () => {
                    this.dataService.settings.getChannel(this.id).single$.subscribe();
                   
                },
            });
    }

    removeFromCategory(group: any) {
        this.modalService
            .dialog({
                title: _('settings.confirm-remove-vendor-from-category'),
                buttons: [
                    { type: 'secondary', label: _('common.cancel') },
                    { type: 'danger', label: _('common.delete'), returnValue: true },
                ],
            })
            .pipe(
                switchMap(response =>
                    response
                        ? this.dataService.settings.removeVendorsFromCategory(group.id, [this.id])
                        : EMPTY,
                ),
                switchMap(() => this.dataService.settings.getChannel(this.id).single$),
            )
            .subscribe(result => {
                this.notificationService.success(_(`settings.remove-vendors-from-category-success`), {
                    vendorCount: 1,
                    categoryName: group.name,
                });
               
            });
    }

}
